<template>
  <d-modal @close="handleClose">
    <d-modal-header>
      <d-modal-title>
        {{ $t('customer.addAddress') }}
      </d-modal-title>
    </d-modal-header>
    <d-modal-body>
      <div class="form-group">
        <label>
          {{ $t('customer.addressCreateFrom') }}:
        </label>

        <d-form-select
          v-model="createFrom"
          :options="createFromOptions" />

        <d-alert
          :show="createFrom === 'invoice'"
          theme="warning"
          class="mt-4">
          {{ $t('customer.invoiceAddressWillBeUsed') }}
        </d-alert>

        <d-alert
          :show="createFrom === 'correspondence'"
          theme="warning"
          class="mt-4">
          {{ $t('customer.correspondenceAddressWillBeUsed') }}
        </d-alert>
      </div>

      <d-form v-show="createFrom === 'new'">
        <validation-observer ref="customer-address-add-form" tag="form">
          <div class="form-group">
            <label>
              {{ $t('settings.country.country') }}:
            </label>

            <validation-provider
              rules="required"
              name="country-id"
              v-slot="{ errors }">
              <d-form-select
                v-model="country_id"
                :state="errors.length ? 'invalid' : null"
                :options="countriesOptions" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('ui.nameAndSurename') }}:
            </label>

            <validation-provider
              rules="required"
              name="name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('ui.emailAddress') }}:
            </label>

            <validation-provider
              rules="email"
              name="email"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="email" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.companyName') }}:
            </label>

            <validation-provider
              rules="required"
              name="company-name"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="company_name" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label for="name">
              {{ $t('customer.street') }}:
            </label>

            <validation-provider
              rules="required"
              name="street"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="street" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.building') }}:
              </label>

              <d-input v-model="building" />
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.houseNumber') }}:
              </label>

              <d-input v-model="locale" />
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.addressLine2') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="line_2"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="line_2" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.addressLine3') }}:
            </label>

            <validation-provider
              rules="max:35"
              name="line_3"
              v-slot="{ errors }">
              <d-input
                :state="errors.length ? 'invalid' : null"
                v-model="line_3" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>

          <div class="d-flex">
            <div class="form-group w-100">
              <label>
                {{ $t('customer.zipCode') }}:
              </label>

              <validation-provider
                :rules="country_id === polandCountryID ? 'required|postal_code_pl' : 'required'"
                name="postcode"
                v-slot="{ errors }">
                <d-input
                  v-if="country_id === polandCountryID"
                  key="invoice-postcode-mask"
                  v-mask="'##-###'"
                  :state="errors.length ? 'invalid' : null"
                  v-model="postcode" />
                <d-input
                  v-else
                  key="invoice-postcode-nomask"
                  :state="errors.length ? 'invalid' : null"
                  v-model="postcode" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>

            <div class="form-group w-100 ml-2">
              <label>
                {{ $t('customer.city') }}:
              </label>

              <validation-provider
                rules="required"
                name="city"
                v-slot="{ errors }">
                <d-input
                  :state="errors.length ? 'invalid' : null"
                  v-model="city" />
                <div class="invalid-feedback">
                  {{ errors[0] }}
                </div>
              </validation-provider>
            </div>
          </div>

          <div class="form-group">
            <label>
              {{ $t('customer.telephone') }}:
            </label>

            <validation-provider
              rules="required"
              name="phone"
              v-slot="{ errors }">
              <d-input
                v-mask="'+## ### ### ### ### ### ### ### ### ###'"
                :state="errors.length ? 'invalid' : null"
                v-model="phone" />
              <div class="invalid-feedback">
                {{ errors[0] }}
              </div>
            </validation-provider>
          </div>
        </validation-observer>
      </d-form>
    </d-modal-body>
    <d-modal-footer>
      <d-button
        class="ml-0"
        theme="danger"
        @click.prevent="cancel">
        {{ $t('ui.cancel') }}
      </d-button>
      <d-button
        class="ml-auto"
        theme="success"
        @click.prevent="validateForm">
        {{ $t('customer.addAddress') }}
      </d-button>
    </d-modal-footer>
  </d-modal>
</template>

<script>
import CommonGetters from './../../utils/CommonGetters.js';
import CustomerPanel from './../../mixins/CustomerPanel.vue';
import FormUtils from './../../utils/FormUtils.js';

export default {
  name: 'customer-address-new-popup',
  mixins: [
    CustomerPanel
  ],
  props: {
    customerID: {
      default: 0,
      type: Number
    },
    countries: {
      default: () => ([]),
      type: Array
    },
    defaultEmail: {
      default: '',
      type: String
    }
  },
  computed: {
    countriesOptions () {
      return [{ value: '', text: this.$t('settings.country.selectCountry') }].concat(CommonGetters.getCountriesForSelect(this.countries));
    },
    createFromOptions () {
      return [
        {
          value: 'new',
          text: this.$t('customer.createNewAddress')
        },
        {
          value: 'correspondence',
          text: this.$t('customer.createAddressCorrespondence')
        },
        {
          value: 'invoice',
          text: this.$t('customer.createAddressInvoice')
        }
      ];
    },
    polandCountryID () {
      let countryPL = this.countries.filter(country => country.code === 'PL');

      if (countryPL[0]) {
        return countryPL[0].id;
      }

      return false;
    }
  },
  data () {
    return {
      name: '',
      company_name: '',
      country_id: '',
      street: '',
      email: this.defaultEmail,
      building: '',
      locale: '',
      line_2: '',
      line_3: '',
      postcode: '',
      city: '',
      phone: '',
      createFrom: 'new'
    };
  },
  methods: {
    handleClose (newAddressID = false) {
      this.$bus.$emit('customer-add-address-popup-close', newAddressID);
    },
    validateForm () {
      if (this.createFrom === 'new') {
        FormUtils.validate(this.$refs['customer-address-add-form'], this.saveAddress);
      } else {
        this.saveAddress();
      }
    },
    saveAddress () {
      if (this.createFrom === 'new') {
        FormUtils.submit({
          $http: this.$http,
          endpoint: this.getApiEndpoint('/customers/addresses/store'),
          formData: {
            customer_id: this.customerID === 0 ? this.$store.getters.getCustomerID : this.customerID,
            name: (this.name).toString().toLocaleUpperCase(),
            email: (this.email).toString().toLocaleUpperCase(),
            company_name: (this.company_name).toString().toLocaleUpperCase(),
            country_id: this.country_id,
            street: (this.street).toString().toLocaleUpperCase(),
            building: (this.building).toString().toLocaleUpperCase(),
            locale: (this.locale).toString().toLocaleUpperCase(),
            line_2: (this.line_2).toString().toLocaleUpperCase(),
            line_3: (this.line_3).toString().toLocaleUpperCase(),
            postcode: (this.postcode).toString().toLocaleUpperCase(),
            city: (this.city).toString().toLocaleUpperCase(),
            phone: this.phone
          },
          successTitle: this.$t('customer.addAddressSuccessTitle'),
          successText: this.$t('customer.addAddressSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('customer.addAddressErrorText'),
          successAction: res => {
            this.$bus.$emit('customer-reload-addresses');
            this.handleClose(res.id);
          }
        });
      } else {
        FormUtils.submit({
          $http: this.$http,
          endpoint: this.getApiEndpoint('/customers/addresses/store-from'),
          formData: {
            customer_id: this.customerID === 0 ? this.$store.getters.getCustomerID : this.customerID,
            create_from: this.createFrom
          },
          successTitle: this.$t('customer.addAddressSuccessTitle'),
          successText: this.$t('customer.addAddressSuccessText'),
          errorTitle: this.$t('ui.errorOccured'),
          errorText: this.$t('customer.addAddressErrorText'),
          successAction: res => {
            this.$bus.$emit('customer-reload-addresses');
            this.handleClose(res.id);
          }
        });
      }
    },
    cancel () {
      this.handleClose();
    }
  }
}
</script>
